/**
 * Helper functions for CreateMeetingPopup component
 */

/**
 * Formats time as HH:MM with zero-padding
 */
export const formatTimeString = (hours: number, minutes: number): string => {
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
}

/**
 * Rounds minutes to next 15-minute interval
 * Returns both rounded minutes and whether to add an hour
 */
export const roundToNext15Minutes = (
  minutes: number
): { roundedMinutes: number; addHour: boolean } => {
  const roundedMinutes = Math.ceil(minutes / 15) * 15
  return {
    roundedMinutes: roundedMinutes >= 60 ? 0 : roundedMinutes,
    addHour: roundedMinutes >= 60,
  }
}

/**
 * Gets current time in specified timezone with 15-minute rounding
 */
export const getCurrentTimeInTimezone = (timezone: string): { hour: number; minute: number } => {
  const now = new Date()

  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })

    const parts = formatter.formatToParts(now)
    const hour = parseInt(parts.find((p) => p.type === 'hour')?.value || '0')
    const minute = parseInt(parts.find((p) => p.type === 'minute')?.value || '0')

    const { roundedMinutes, addHour } = roundToNext15Minutes(minute)

    return {
      hour: addHour ? hour + 1 : hour,
      minute: roundedMinutes,
    }
  } catch (error) {
    // Fallback to local time
    const { roundedMinutes, addHour } = roundToNext15Minutes(now.getMinutes())

    return {
      hour: addHour ? now.getHours() + 1 : now.getHours(),
      minute: roundedMinutes,
    }
  }
}

/**
 * Adds minutes to a time string and returns new time
 */
export const addMinutesToTime = (timeString: string, minutesToAdd: number): string => {
  const [hours, minutes] = timeString.split(':').map(Number)
  const endDate = new Date(2000, 0, 1, hours, minutes)
  endDate.setMinutes(endDate.getMinutes() + minutesToAdd)
  return formatTimeString(endDate.getHours(), endDate.getMinutes())
}

/**
 * Formats minute with leading zero
 */
export const formatMinute = (minute: number): string => {
  return minute.toString().padStart(2, '0')
}

/**
 * Gets timezone display string with offset
 */
export const getTimezoneDisplay = (timezone: string): string => {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'longOffset',
    })
    const parts = formatter.formatToParts(new Date())
    const offsetPart = parts.find((part) => part.type === 'timeZoneName')
    const formattedOffset = offsetPart ? offsetPart.value : 'GMT+00:00'

    return `(${formattedOffset}) ${timezone.replace('_', ' ')}`
  } catch (error) {
    return '(GMT+00:00) UTC'
  }
}

/**
 * Filters time slots to show only future times for today in local timezone
 */
export const getValidStartTimes = (
  selectedDate: Date | null,
  selectedTimezone: string,
  deviceTimezone: string
): { value: string; display: string }[] => {
  const allSlots = generateTimeSlots()

  if (!selectedDate) return allSlots

  const today = new Date()
  const isToday = selectedDate.toDateString() === today.toDateString()

  if (!isToday) return allSlots
  if (selectedTimezone !== deviceTimezone) return allSlots

  const now = new Date()
  const currentHours = now.getHours()
  const currentMinutes = now.getMinutes()
  const currentTimeInMinutes = currentHours * 60 + currentMinutes

  return allSlots.filter((slot) => {
    const [hours, minutes] = slot.value.split(':').map(Number)
    const slotTimeInMinutes = hours * 60 + minutes
    return slotTimeInMinutes > currentTimeInMinutes
  })
}

/**
 * Filters time slots to show only times after start time
 */
export const getValidEndTimes = (startTime: string): { value: string; display: string }[] => {
  const allSlots = generateTimeSlots()
  if (!startTime) return allSlots

  return allSlots.filter((slot) => slot.value > startTime)
}

/**
 * Formats date for display
 */
export const formatDate = (date: Date): string => {
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

/**
 * Formats time for display (12-hour format)
 */
export const formatTimeDisplay = (timeString: string): string => {
  if (!timeString) return ''
  const [hours, minutes] = timeString.split(':').map(Number)

  // Simple 12-hour format conversion without timezone conversion
  const displayHour = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours
  const displayPeriod = hours < 12 ? 'AM' : 'PM'
  const displayMinute = formatMinute(minutes)

  return `${displayHour}:${displayMinute} ${displayPeriod}`
}

/**
 * Validates email address format
 */
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

/**
 * Generates time slots in 15-minute intervals
 */
export const generateTimeSlots = (): { value: string; display: string }[] => {
  const slots: { value: string; display: string }[] = []

  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      const timeString = formatTimeString(hour, minute)

      // Simple 12-hour format display (not timezone converted, just formatted)
      const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour
      const displayPeriod = hour < 12 ? 'AM' : 'PM'
      const displayMinute = formatMinute(minute)
      const displayTime = `${displayHour}:${displayMinute} ${displayPeriod}`

      slots.push({ value: timeString, display: displayTime })
    }
  }
  return slots
}

/**
 * Gets user role label based on type
 */
export const getUserRoleLabel = (
  userType?: string,
  userId?: string,
  currentUserId?: string
): string => {
  if (userId === currentUserId) {
    return 'Organizer'
  }

  switch (userType) {
    case 'OWNER':
      return 'Business Owner'
    case 'CO_OWNER':
      return 'Co-Owner'
    case 'ADMIN':
      return 'Admin'
    case 'MEMBER':
      return 'Team Member'
    default:
      return 'Guest'
  }
}
